/**
 * Setting up i18next.
 *
 * Configuration worth knowing about:
 *
 * - **No key fallback to the key itself.** A missing translation renders the
 *   English string, not `nav.signIn`. A user seeing a dotted key knows
 *   something is broken; a user seeing English simply sees English.
 * - **No interpolation escaping.** React escapes everything it renders, so
 *   i18next escaping again produces `&amp;` in visible text — the classic
 *   double-escape bug.
 * - **Missing keys are reported in development.** Silence is how a translation
 *   gap survives to production.
 */

import i18next, { type i18n as I18n } from "i18next";
import { initReactI18next } from "react-i18next";

import commonAr from "./locales/ar/common.json";
import commonEn from "./locales/en/common.json";
import commonEs from "./locales/es/common.json";
import commonFr from "./locales/fr/common.json";

import {
  applyLocaleToDocument,
  DEFAULT_LOCALE,
  isSupported,
  negotiateLocale,
  SUPPORTED_LOCALES,
  type Locale,
} from "./config";
import { registerNamespace, resourcesForI18next } from "./registry";

export const LOCALE_STORAGE_KEY = "hardauto.locale";

/** Whether this is a development build. Guarded: `import.meta.env` is a Vite
 *  construct and is absent in a plain Node test run. */
function isDevelopment(): boolean {
  try {
    return import.meta.env?.DEV === true;
  } catch {
    return false;
  }
}

/** `core-i18n` owns the `common` namespace: shared verbs, states and errors. */
export function registerCommonNamespace(): void {
  registerNamespace("common", {
    en: commonEn,
    fr: commonFr,
    es: commonEs,
    ar: commonAr,
  });
}

/**
 * Read the stored locale.
 *
 * Guarded like every other storage access: private mode and blocked site data
 * both throw, and a language preference is not worth a blank page.
 */
export function readStoredLocale(): Locale | null {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return stored && isSupported(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function storeLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Applies for this page; simply will not be remembered.
  }
}

/**
 * Decide which locale to start in.
 *
 * An explicit stored choice wins over the browser's list, always. A user who
 * picked French on an English machine chose deliberately, and quietly
 * overriding them on the next visit is the same mistake the theme toggle
 * avoids.
 */
export function initialLocale(navigatorLanguages: readonly string[] = []): Locale {
  return readStoredLocale() ?? negotiateLocale(navigatorLanguages);
}

export async function createI18n(locale: Locale = DEFAULT_LOCALE): Promise<I18n> {
  await i18next.use(initReactI18next).init({
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    defaultNS: "common",
    ns: Object.keys(resourcesForI18next()[DEFAULT_LOCALE] ?? {}),
    resources: resourcesForI18next(),
    interpolation: {
      // React already escapes what it renders. Escaping here too produces
      // `&amp;` in visible text.
      escapeValue: false,
    },
    saveMissing: false,
    returnNull: false,
    // Spread rather than assigning `undefined`: `exactOptionalPropertyTypes`
    // treats an explicit undefined as a different thing from an absent key,
    // and i18next's options type says absent.
    ...(isDevelopment()
      ? {
          // Report gaps loudly in development. Silence is how a missing
          // translation reaches production.
          missingKeyHandler: (lngs: readonly string[], ns: string, key: string) => {
            console.warn(`[i18n] missing key "${ns}:${key}" for ${lngs.join(",")}`);
          },
        }
      : {}),
  });
  return i18next;
}

/** Switch language, updating storage and the document together. */
export async function changeLocale(instance: I18n, locale: Locale): Promise<void> {
  await instance.changeLanguage(locale);
  storeLocale(locale);
  // `lang` and `dir` must move with the strings: a screen reader picks its
  // voice from `lang`, and Arabic in a left-to-right document is unreadable.
  applyLocaleToDocument(locale);
}
